import SlideManager from 'slidemanager'
import Block from './Block'
import TweenMax, { Expo } from 'gsap/TweenMax'
import { Power3 } from 'gsap'

export default class NewsSlider extends Block {
  constructor(el, destroyLast) {
    super(el, destroyLast)

    this.el = el
    this.destroyLast = destroyLast
    this.isSliding = false

    this.splitTitle(this.titles)
    this.splitContent(this.excerpts)
    this.setValues()
    this.createSlider()
    this.initSlider()
  }

  bindMethods() {
    super.bindMethods()

    this.nextSlide = this.nextSlide.bind(this)
    this.prevSlide = this.prevSlide.bind(this)
    this.hideOldSlide = this.hideOldSlide.bind(this)
    this.showNewSlide = this.showNewSlide.bind(this)
    this.onCompleteAll = this.onCompleteAll.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
  }

  getElems() {
    super.getElems()

    this.slideWrapper = this.el.querySelector('.slide-wrapper')
    this.slideContentWrapper = this.el.querySelector('.latest-news__slide-content')
    this.items = this.el.querySelectorAll('.latest-news__slider-item')
    this.contents = this.el.querySelectorAll('.latest-news__slide-content--item > .item__content')
    this.contentItems = this.el.querySelectorAll('.latest-news__slide-content--item')
    this.titles = this.el.querySelectorAll('.item__content-title')
    this.excerpts = this.el.querySelectorAll('.item__content-excerpt')
    this.buttons = this.slideContentWrapper.querySelectorAll('.cta')
    this.pagerRight = this.el.querySelector('.pager-next')
    this.pagerLeft = this.el.querySelector('.pager-prev')
  }

  events() {
    super.events()

    this.pagerRight.addEventListener('click', this.nextSlide)
    this.pagerRight.addEventListener('mouseenter', this.onMouseEnter.bind(this, 'next'))
    this.pagerRight.addEventListener('mouseleave', this.onMouseLeave.bind(this, 'next'))
    this.pagerLeft.addEventListener('click', this.prevSlide)
    this.pagerLeft.addEventListener('mouseenter', this.onMouseEnter.bind(this, 'prev'))
    this.pagerLeft.addEventListener('mouseleave', this.onMouseLeave.bind(this, 'prev'))
  }

  splitContent(content) {
    if (!content) return

    for (let i = 0; i < content.length; i++) {
      this.splittedContent = new SplitText(content[i], {
        type: 'lines',
        linesClass: 'line'
      })
      this.wrap()
    }
  }

  wrap() {
    for (let i = 0; i < this.splittedContent.lines.length; i++) {
      const inner = this.splittedContent.lines[i].innerHTML

      this.splittedContent.lines[i].innerHTML = '<span>' + inner + '</span>'
    }
  }

  splitTitle(title) {
    if (!title) return

    for (let i = 0; i < title.length; i++) {
      this.splittedTitle = new SplitText(title[i], {
        type: 'lines',
        linesClass: 'line'
      })
      this.wrapTitle()
    }
  }

  wrapTitle() {
    for (let i = 0; i < this.splittedTitle.lines.length; i++) {
      const inner = this.splittedTitle.lines[i].innerHTML

      this.splittedTitle.lines[i].innerHTML = '<span>' + inner + '</span>'
    }
  }

  createSlider() {
    this.slider = new SlideManager({
      el: this.slideWrapper,
      loop: true,
      callback: (event) => {
        this.oldIndex = event.previous
        this.currentIndex = event.current
        this.direction = event.direction

        this.transform = Number(-this.itemWidth * this.currentIndex - this.offset * this.currentIndex)

        this.hideOldSlide()
          .then(this.showNewSlide)
          .then(() => {
            this.slider.done()
          })
      }
    })
  }

  setValues() {
    this.itemWidth = this.items[0].offsetWidth
    this.offset = parseFloat(window.getComputedStyle(this.items[0]).getPropertyValue('margin-right'))
  }

  initSlider() {
    this.items[0].classList.add('active')
    this.contentItems[0].classList.add('active')
    for (let i = 0; i < this.contentItems.length; i++) this.contentItems[i].style.display = 'none'
    this.contentItems[0].style.display = 'block'
    this.pagerLeft.classList.add('hide')
  }

  hideOldSlide() {
    return new Promise((resolve) => {
      const titles = this.titles[this.oldIndex].querySelectorAll('.line > span')
      const lines = this.excerpts[this.oldIndex].querySelectorAll('.line > span')

      this.items[this.oldIndex].classList.remove('active')
      this.contentItems[this.oldIndex].classList.remove('active')

      this.currentIndex === 0 ? this.pagerLeft.classList.add('hide') : this.pagerLeft.classList.remove('hide')

      TweenMax.staggerTo(lines, 1, {
        y: '-100%',
        ease: Expo.easeOut
      }, 0.05)
      TweenMax.staggerTo(titles, 1, {
        y: '-100%',
        ease: Expo.easeOut
      }, 0.05, this.onCompleteAll)
      TweenMax.to(this.buttons[this.oldIndex], 1, {
        alpha: 0,
        ease: Expo.easeOut
      })

      resolve()
    })
  }

  showNewSlide() {
    return new Promise((resolve) => {
      const titles = this.titles[this.currentIndex].querySelectorAll('.line > span')
      const lines = this.excerpts[this.currentIndex].querySelectorAll('.line > span')

      this.isSliding = true

      const tl = new TimelineLite()

      tl.addLabel('start', 0)
      tl.addLabel('delayed', 1)

      tl.to(this.items, 1, {
        x: this.transform,
        ease: Power3.easeOut,
        onStart: () => {
          this.items[this.currentIndex].classList.add('active')
          this.contentItems[this.currentIndex].classList.add('active')
        },
        onComplete: () => {
          this.isSliding = false
          resolve()
        }
      }, 'start')
      tl.staggerTo(lines, 1, {
        y: '0%',
        ease: Expo.easeInOut
      }, 0.05, 'delayed')
      tl.staggerTo(titles, 1, {
        y: '0%',
        ease: Expo.easeInOut
      }, 0.05, 'delayed')
      tl.to(this.buttons[this.currentIndex], 1, {
        alpha: 1,
        ease: Expo.easeOut
      }, 1.5)
    })
  }

  onCompleteAll() {
    const oldTitle = this.titles[this.oldIndex].querySelectorAll('.line > span')
    const oldLines = this.excerpts[this.oldIndex].querySelectorAll('.line > span')

    this.contentItems[this.oldIndex].style.display = 'none'
    this.contentItems[this.currentIndex].style.display = 'block'

    TweenMax.set(oldTitle, { y: '100%' })
    TweenMax.set(oldLines, { y: '100%' })
  }

  onMouseEnter(direction) {
    if (!this.currentIndex) this.currentIndex = 0
    this.transform = this.transform ? this.transform : 0

    if (direction === 'next') this.slideIndex = this.currentIndex === this.items.length - 1 ? 0 : this.currentIndex + 1
    else this.slideIndex = this.currentIndex === this.items.length + 1 ? 0 : this.currentIndex - 1

    TweenMax.to(this.items[this.slideIndex], 1, {
      x: direction === 'next' ? this.transform - 20 : this.transform + 20,
      ease: Expo.easeOut
    })
  }

  onMouseLeave(direction) {
    if (this.isSliding) return
    if (!this.currentIndex) this.currentIndex = 0

    if (direction === 'next') this.slideIndex = this.currentIndex === this.items.length - 1 ? 0 : this.currentIndex + 1
    else this.slideIndex = this.currentIndex === this.items.length + 1 ? 0 : this.currentIndex - 1

    TweenMax.to(this.items[this.slideIndex], 1, {
      x: this.transform ? this.transform : 0,
      ease: Expo.easeOut
    })
  }

  prevSlide() {
    this.slider.prev()
  }

  nextSlide() {
    this.slider.next()
  }

  resize() {
    super.resize()

    this.setValues()
    this.slider.goTo(0)
  }
}



// WEBPACK FOOTER //
// ./scripts/blocks/NewsSlider.js